import { AlertTriangle, CircleSlash, Link2, Link2Off, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { differenceInCalendarDays } from 'date-fns';
import { dbCalendarDayInOrgZone } from '@/lib/dateUtils';
import type { BankConnectionAccountWire, BankConnectionWire } from '@releve/shared';

/**
 * The bank-connection state machine behind one ledger account's feed, plus the
 * single colour/label table its consumers render it with.
 *
 * Pure by design — no React, no query, no i18n. It lives apart from any one
 * surface because several read the same derivation (the toolbar button, the
 * status popover, the card marker, the Bank feed settings section), and because
 * a non-component export from a component file trips
 * `react-refresh/only-export-components`.
 *
 * DELIBERATELY PLAIN TEXT PLUS A SMALL LUCIDE ICON, NOT A BADGE. The surface
 * directly above the popover — `FeedContextBar` — renders every fact as plain
 * `text-sm` with muted labels and hairline dividers, and escalates to a tinted
 * bordered container rather than a pill. A status pill there would be the only
 * badge on the page.
 *
 * Colour is semantic-token only (docs/STYLES.md, docs/COLORS.md forbids brand
 * `releve-*` for state):
 *
 * | State                 | Token         |
 * |-----------------------|---------------|
 * | Connected             | `info`        |
 * | Expiring soon         | `warning`     |
 * | Expired / revoked     | `secondary`   |
 * | Sync failed           | `destructive` |
 *
 * Expired is deliberately NOT destructive: consent lapsing is an ordinary state
 * with a Reconnect button, and destructive chrome would contradict that.
 *
 * Connected is a LINK glyph in `info` teal, not a green check: the marker says
 * "this account is wired to a bank", a fact about the feed's plumbing, and a
 * check mark read as "reconciled / all good" beside a balance that may well
 * still have lines to review. `info` is the informational token, and the icon
 * is the same `Link2` family the two no-feed states use (`Link2Off`), so
 * connected and unconnected are visibly the same question answered two ways.
 */

/** How close to `valid_until` we start calling the consent "expiring soon". */
export const CONSENT_EXPIRY_WARNING_DAYS = 14;

/**
 * What this ledger account's feed connection looks like right now. `state` is
 * the visual; `action` is what the one toolbar button does, and the two are not
 * one-to-one — a failed sync stays retryable (`sync`) while a failed
 * authorization needs the bank again (`reconnect`).
 */
export type FeedConnectionState =
  | 'unconnected'
  | 'unlinked'
  | 'connected'
  | 'expiring'
  | 'expired'
  /**
   * Consent is live but THIS account was not in the bank's latest answer —
   * the user left it unticked on the bank's consent screen (each
   * authorisation is a fresh consent, never a union with the last one), or
   * it was closed. Same Reconnect action as `expired`, different reason, so
   * it gets its own label: "Consent expired" told the user to fix the wrong
   * thing (Clay, 2026-09-07).
   */
  | 'notInConsent'
  | 'revoked'
  | 'failed';

export type FeedConnectionAction = 'connect' | 'link' | 'sync' | 'reconnect';

export interface FeedConnectionView {
  state: FeedConnectionState;
  action: FeedConnectionAction;
  /** The connection this ledger account's bank account belongs to, if linked. */
  connection: BankConnectionWire | null;
  /** The bank account mapped to this ledger account, if any. */
  account: BankConnectionAccountWire | null;
  /**
   * A live connection this org can link a further account through WITHOUT another
   * trip to the bank. Drives the `link` action (Dialog B, skipping the redirect).
   */
  linkableConnection: BankConnectionWire | null;
  /** Whole days until consent expiry, in the org's zone. Null when unknown. */
  daysUntilExpiry: number | null;
}

/**
 * The most recently authorized live connection — the one an account picker can
 * list without sending the user back to their bank.
 *
 * Written once and shared: the derivation below needs it for every ledger
 * account, and the post-callback set-up dialog needs it for the consent the
 * user has just come back from.
 */
export function pickLinkableConnection(
  connections: BankConnectionWire[] | undefined,
): BankConnectionWire | null {
  return (
    (connections ?? [])
      .filter((c) => c.status === 'authorized')
      .sort((a, b) => (b.authorized_at ?? '').localeCompare(a.authorized_at ?? ''))[0] ?? null
  );
}

/**
 * Pure. Given the org's connections and this ledger account's id, decide what the
 * toolbar shows and does.
 *
 * `now` is injectable so the expiry boundary is testable without freezing the
 * clock globally.
 *
 * `isConnectFlowTarget` says this ledger account is the one a connect flow
 * started from (the `connect=` resume marker). It is what separates "finish what
 * you started here" (`unlinked`, a warning) from "this account simply has no
 * feed" (`unconnected`, an invitation) — see the `unlinked` note below.
 */
export function deriveFeedConnectionView(
  connections: BankConnectionWire[] | undefined,
  bankAccountId: string,
  orgTimeZone: string | null,
  now: Date = new Date(),
  isConnectFlowTarget = false,
): FeedConnectionView {
  const all = connections ?? [];

  let connection: BankConnectionWire | null = null;
  let account: BankConnectionAccountWire | null = null;
  for (const candidate of all) {
    const match = candidate.accounts.find((a) => a.chart_of_accounts_id === bankAccountId);
    if (match) {
      connection = candidate;
      account = match;
      break;
    }
  }

  const linkableConnection = pickLinkableConnection(all);

  const daysUntilExpiry = daysUntilConsentExpiry(connection?.valid_until ?? null, orgTimeZone, now);

  if (!connection || !account) {
    // "Finish connecting THIS account" is a real, unfinished piece of work —
    // but ONLY for the account the connect flow started from. Nothing else
    // about the org's connections is a state of this account.
    //
    // In particular, NOT "the live connection still holds a bank account mapped
    // nowhere": that is an org-wide fact, and it is a perfectly ordinary one —
    // a two-account ING consent whose savings account is deliberately left
    // unmapped is finished, not half-done. Deriving `unlinked` from it painted
    // every OTHER unlinked feed account in the org (a second bank, a CAMT-fed
    // one) with a warning reading "your bank is already connected", plus a
    // status line naming ING — false for an account ING does not feed.
    //
    // Those stay `unconnected`: an invitation, info-toned, and no status line
    // naming someone else's bank. The button still skips the trip to the bank
    // whenever consent is held — that is the `action` below, not the state.
    const state: FeedConnectionState =
      linkableConnection && isConnectFlowTarget ? 'unlinked' : 'unconnected';
    return {
      state,
      // Both roads lead to "Connect bank feed" on the button; only the dialog
      // differs — a live connection means we already have consent and can go
      // straight to picking the account.
      action: linkableConnection ? 'link' : 'connect',
      connection: null,
      account: null,
      linkableConnection,
      daysUntilExpiry: null,
    };
  }

  // `pending` means an authorization was started and the PSU never came back, so
  // there is nothing to sync with — same remedy as an expired consent.
  if (connection.status === 'revoked') {
    return { state: 'revoked', action: 'reconnect', connection, account, linkableConnection, daysUntilExpiry };
  }
  if (connection.status === 'error') {
    // Set only when creating the session failed — the consent never completed.
    return { state: 'failed', action: 'reconnect', connection, account, linkableConnection, daysUntilExpiry };
  }
  if (connection.status === 'expired' || connection.status === 'pending') {
    return { state: 'expired', action: 'reconnect', connection, account, linkableConnection, daysUntilExpiry };
  }
  if (!account.is_in_current_consent) {
    return { state: 'notInConsent', action: 'reconnect', connection, account, linkableConnection, daysUntilExpiry };
  }

  // Authorized. A `last_error` surviving under `authorized` is by definition a
  // RETRYABLE sync failure, so the button stays Sync. The sync route sorts the
  // two apart before we ever see them: a consent-class failure
  // (EXPIRED_SESSION / CLOSED_SESSION → `expired`, REVOKED_SESSION → `revoked`)
  // is stored as that `status` — and every `account_uid` nulled — so it lands in
  // the branches above and offers Reconnect. Only a retryable error leaves
  // `status` alone, and a clean run clears `last_error` outright.
  if (connection.last_error) {
    return { state: 'failed', action: 'sync', connection, account, linkableConnection, daysUntilExpiry };
  }
  if (daysUntilExpiry !== null && daysUntilExpiry <= CONSENT_EXPIRY_WARNING_DAYS) {
    return { state: 'expiring', action: 'sync', connection, account, linkableConnection, daysUntilExpiry };
  }
  return { state: 'connected', action: 'sync', connection, account, linkableConnection, daysUntilExpiry };
}

/**
 * Whole days from today to consent expiry, both resolved as calendar days IN THE
 * ORG'S ZONE. Never `new Date(valid_until) - Date.now()`: `valid_until` is a
 * `timestamptz`, and a raw millisecond subtraction answers the question in the
 * viewer's browser zone rather than the organization's (docs/DATES.md).
 */
function daysUntilConsentExpiry(
  validUntil: string | null,
  orgTimeZone: string | null,
  now: Date,
): number | null {
  if (!validUntil) return null;
  const expiryDay = dbCalendarDayInOrgZone(validUntil, orgTimeZone);
  const today = dbCalendarDayInOrgZone(now.toISOString(), orgTimeZone);
  if (!expiryDay || !today) return null;
  return differenceInCalendarDays(expiryDay, today);
}

export const STATE_PRESENTATION: Record<
  FeedConnectionState,
  { icon: LucideIcon; className: string; labelKey: string }
> = {
  // `text-secondary` is a light background hue in this palette; the legible
  // "inactive" text token is `muted-foreground`, which is what the secondary
  // row of the status table means on text.
  unconnected: { icon: Link2Off, className: 'text-muted-foreground', labelKey: 'bankAccounts.sync.status.unconnected' },
  unlinked: { icon: Link2Off, className: 'text-muted-foreground', labelKey: 'bankAccounts.sync.status.unlinked' },
  connected: { icon: Link2, className: 'text-info', labelKey: 'bankAccounts.sync.status.connected' },
  expiring: { icon: AlertTriangle, className: 'text-warning', labelKey: 'bankAccounts.sync.status.expiring' },
  expired: { icon: CircleSlash, className: 'text-muted-foreground', labelKey: 'bankAccounts.sync.status.expired' },
  notInConsent: { icon: CircleSlash, className: 'text-muted-foreground', labelKey: 'bankAccounts.sync.status.notInConsent' },
  revoked: { icon: CircleSlash, className: 'text-muted-foreground', labelKey: 'bankAccounts.sync.status.revoked' },
  failed: { icon: XCircle, className: 'text-destructive', labelKey: 'bankAccounts.sync.status.failed' },
};
